/**
 * 製造年月の表示・入力ヘルパー（WEB管理画面用）。
 *
 * DBに保存されている形式（バックエンドが正規化した後の値）を正本として扱う:
 * - `YYYY/MM`   … 年月ともに判明
 * - `YYYY/不明` … 年は判明・月は不明（アプリの「月不明」チェック）
 * - `不明` / `不詳` … 年月ともに不明
 * - `''` … 未入力
 *
 * 注意: 内機の serial_number は 2026-05 以前のアプリでは「製造番号」の自由入力
 * だったため、上記のどれにも当てはまらない任意文字列が入っている場合がある。
 * そのため書式が不正でも値を書き換えたり保存を止めたりはしない。
 */

const UNKNOWN_MONTH_LABEL = "不明";

/** 年月ともに不明（`不明` / `不詳`）か。 */
export function isWholeUnknown(value: string): boolean {
  return value === "不明" || value === "不詳";
}

/**
 * 「月不明」モードか。
 * 年の入力途中（0〜3桁）も含めて true を返すため、チェックを入れた直後に
 * チェックが外れて見える状態にならない。
 */
export function isMonthUnknown(value: string): boolean {
  return /^\d{0,4}\/不明$/.test(value);
}

/** 「月不明」として年4桁まで入力し終えているか。 */
export function isCompleteMonthUnknown(value: string): boolean {
  return /^\d{4}\/不明$/.test(value);
}

/**
 * 「月不明」モードで年が4桁揃っていない場合のエラー文を返す。問題なければ null。
 * 管理者が「月不明」を選んだ場合だけを対象にするため、旧アプリの製造番号など
 * 書式外の既存値には影響しない。
 */
export function validateMonthUnknownInput(value: string): string | null {
  if (!isMonthUnknown(value)) return null;
  if (isCompleteMonthUnknown(value)) return null;
  return "製造年を4桁で入力してください";
}

/** 「月不明」モードの入力欄に表示する年部分（0〜4桁）。 */
export function yearPartOf(value: string): string {
  if (isWholeUnknown(value)) return "";
  const monthUnknown = value.match(/^(\d{0,4})\/不明$/);
  if (monthUnknown) return monthUnknown[1];
  // `YYYY/MM` と `YYYYMM` の両形式から年を取り出す
  const slash = value.match(/^(\d{4})\//);
  if (slash) return slash[1];
  if (/^\d{4,6}$/.test(value)) return value.slice(0, 4);
  return "";
}

/** 「月不明」モードで入力された年部分から保存形式を組み立てる。 */
export function buildMonthUnknown(yearPart: string): string {
  return `${yearPart}/${UNKNOWN_MONTH_LABEL}`;
}

/**
 * 手入力値のうち「月00」を「月不明」の意図として検出する。
 * 自動変換はせず、入力欄の案内表示にのみ使う。
 * （内機の serial_number には旧アプリの製造番号が入り得るため、
 *   6桁数値を機械的に年月へ読み替えるのは危険）
 */
export function looksLikeUnknownMonthTypo(value: string): boolean {
  return /^\d{4}\/?00$/.test(value);
}
